/**
 * DoubleRowForm - Form for double row (back-to-back) hangar template
 *
 * Two rows of box units sharing a spine wall (duplex-style).
 * Supports symmetric mode (both rows identical) and custom mode (independent config).
 */

import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState, useRef } from 'react'
import { ChevronDown, ChevronRight, Trash2, Loader2, Layers, ArrowLeftRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import {
  doubleRowParamsSchema,
  DEFAULT_DOUBLE_ROW_VALUES,
  type DoubleRowParams,
  type LayoutMode,
  getEffectiveRowB,
  getUnitDoors as getUnitDoorsFromParams,
  calculateBuildingDepth,
} from '@/lib/templates/double-row/params'
import { calculateDoubleRowLayout, type SuggestedFix } from '@/lib/templates/double-row/calculations'
import { DOOR_TYPE_DEFAULTS, type DoorType } from '@/lib/templates/types'
import { UnitLayoutPreview } from '../shared/UnitLayoutPreview'
import { ConstraintWarning } from '../shared/ConstraintWarning'
import { DoubleRowUnitDoorEditor } from './DoubleRowUnitDoorEditor'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { RemoveAreaButton } from '../shared/RemoveAreaButton'
import { Form } from '@/components/ui/form'
import { PricingFeaturesSection } from './PricingFeaturesSection'
import { pricingFieldsSchema, defaultPricingValues, type PricingFormFields } from '@/lib/schemas/pricing-fields'
import type { UnitDoorsConfig } from '@/lib/templates/types'
import { UNIT_TYPE_LABELS, type UnitType } from '@/types/units'
import { ALLOWED_UNIT_TYPES_FOR_AREA } from '@/types/type-defaults'

interface DoubleRowFormProps {
  initialValues?: Partial<DoubleRowParams>
  onValuesChange?: (values: DoubleRowParams) => void
  onSave: (values: DoubleRowParams, pricing?: PricingFormFields) => void
  onCancel: () => void
  isCreating?: boolean
  isSaving?: boolean
  previewLatitude?: number
  previewLongitude?: number
  isPlaced?: boolean  // When false, building follows cursor - disable save until placed
  /** Area ID for delete functionality (edit mode only) */
  areaId?: string
  /** Area name for delete confirmation message */
  areaName?: string
  /** Called after area is successfully removed */
  onRemoved?: () => void
  /** Initial pricing values (for edit mode) */
  initialPricing?: Partial<PricingFormFields>
}

export function DoubleRowForm({
  initialValues,
  onValuesChange,
  onSave,
  onCancel,
  isCreating = true,
  isSaving = false,
  previewLatitude,
  previewLongitude,
  isPlaced = true,
  areaId,
  areaName,
  onRemoved,
  initialPricing,
}: DoubleRowFormProps) {
  const [openRowAUnits, setOpenRowAUnits] = useState<Set<number>>(new Set())
  const [openRowBUnits, setOpenRowBUnits] = useState<Set<number>>(new Set())
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['row_a']))
  const isInitialMount = useRef(true)

  // Get store values and actions
  const {
    doubleRowFormValues,
        updateDoubleRowFormValues,
      } = useBuildingEditorStore()

  const form = useForm<DoubleRowParams>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(doubleRowParamsSchema) as unknown as Resolver<DoubleRowParams>,
    defaultValues: {
      ...DEFAULT_DOUBLE_ROW_VALUES,
      ...initialValues,
      // Use store values if available (for resuming edit)
      ...(doubleRowFormValues || {}),
    },
  })

  const pricingForm = useForm<PricingFormFields>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(pricingFieldsSchema) as unknown as Resolver<PricingFormFields>,
    defaultValues: { ...defaultPricingValues, ...initialPricing },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form

  // Watch all values
  const watchedValues = watch()

  // Get effective row B (mirrors row A in symmetric mode)
  const effectiveRowB = useMemo(() => {
    return getEffectiveRowB(watchedValues)
  }, [watchedValues.layout_mode, watchedValues.row_a, watchedValues.row_b])

  // Calculate layout with current values
  const layoutResult = useMemo(() => {
    return calculateDoubleRowLayout(watchedValues)
  }, [
    watchedValues.width_ft,
    watchedValues.row_a.unit_count,
    watchedValues.row_b.unit_count,
    watchedValues.partition_wall_thickness_ft,
    watchedValues.layout_mode,
    watchedValues.row_a_overrides,
    watchedValues.row_b_overrides,
  ])

  // Calculate total building depth
  const buildingDepth = useMemo(() => {
    return calculateBuildingDepth(watchedValues)
  }, [watchedValues.row_a.depth_ft, watchedValues.row_b.depth_ft, watchedValues.shared_wall_thickness_ft, watchedValues.layout_mode])

  // Sync form values to store for preview updates
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    updateDoubleRowFormValues(watchedValues)
  }, [
    watchedValues.width_ft,
    watchedValues.height_ft,
    watchedValues.row_a.depth_ft,
    watchedValues.row_a.unit_count,
    watchedValues.row_b.depth_ft,
    watchedValues.row_b.unit_count,
    watchedValues.layout_mode,
    watchedValues.shared_wall_thickness_ft,
    watchedValues.partition_wall_thickness_ft,
    watchedValues.default_door_type,
    watchedValues.default_door_width_ft,
    watchedValues.default_door_height_ft,
    watchedValues.floor_color,
    watchedValues.wall_color,
    watchedValues.bearing_deg,
    watchedValues.name,
    watchedValues.code,
    JSON.stringify(watchedValues.row_a_overrides),
    JSON.stringify(watchedValues.row_b_overrides),
    updateDoubleRowFormValues,
  ])

  // Sync bearing from store to form when changed externally (e.g., rotation ring)
  useEffect(() => {
    const storeBearing = doubleRowFormValues?.bearing_deg
    const formBearing = watchedValues.bearing_deg

    if (storeBearing !== undefined && Math.abs(storeBearing - formBearing) > 0.1) {
      setValue('bearing_deg', storeBearing)
    }
  }, [doubleRowFormValues?.bearing_deg, setValue])

  // Notify parent of value changes
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(watchedValues)
    }
  }, [watchedValues, onValuesChange])

  // Get door constraints for current door type
  const doorConstraints = useMemo(() => {
    return DOOR_TYPE_DEFAULTS[watchedValues.default_door_type as DoorType]
  }, [watchedValues.default_door_type])

  // Handle layout mode change
  const handleLayoutModeChange = (mode: LayoutMode) => {
    setValue('layout_mode', mode)
    if (mode === 'symmetric') {
      // Sync row B to row A
      setValue('row_b', { ...watchedValues.row_a })
      setValue('row_b_overrides', [])
    }
  }

  // Handle row A unit count change
  const handleRowAUnitCountChange = (value: number[]) => {
    setValue('row_a.unit_count', value[0])
    setValue('row_a_overrides', [])
    if (watchedValues.layout_mode === 'symmetric') {
      setValue('row_b.unit_count', value[0])
      setValue('row_b_overrides', [])
    }
  }

  // Handle row B unit count change (custom mode only)
  const handleRowBUnitCountChange = (value: number[]) => {
    setValue('row_b.unit_count', value[0])
    setValue('row_b_overrides', [])
  }

  // Handle suggested fix
  const handleApplyFix = (fix: SuggestedFix) => {
    switch (fix.type) {
      case 'expand_building':
        if (fix.newValue) {
          setValue('width_ft', Math.ceil(fix.newValue))
        }
        break
      case 'redistribute':
        if (fix.row === 'A' || fix.row === 'both') {
          setValue('row_a_overrides', [])
        }
        if (fix.row === 'B' || fix.row === 'both') {
          setValue('row_b_overrides', [])
        }
        break
      case 'reduce_count':
        if (fix.newValue && (fix.row === 'A' || fix.row === 'both')) {
          setValue('row_a.unit_count', fix.newValue)
          setValue('row_a_overrides', [])
        }
        if (fix.newValue && (fix.row === 'B' || fix.row === 'both')) {
          setValue('row_b.unit_count', fix.newValue)
          setValue('row_b_overrides', [])
        }
        break
    }
  }

  // Toggle section
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  // Row A unit helpers
  const toggleRowAUnit = (index: number) => {
    const newOpen = new Set(openRowAUnits)
    if (newOpen.has(index)) {
      newOpen.delete(index)
    } else {
      newOpen.add(index)
    }
    setOpenRowAUnits(newOpen)
  }

  const getRowAOverride = (index: number) => {
    return watchedValues.row_a_overrides?.find(o => o.index === index)
  }

  const addRowAWidthOverride = (index: number, width: number) => {
    const currentOverrides = watchedValues.row_a_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], width_ft: width }
      setValue('row_a_overrides', updated)
    } else {
      setValue('row_a_overrides', [...currentOverrides, { index, width_ft: width }])
    }
  }

  const addRowATypeOverride = (index: number, type: UnitType) => {
    const currentOverrides = watchedValues.row_a_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], type }
      setValue('row_a_overrides', updated)
    } else {
      setValue('row_a_overrides', [...currentOverrides, { index, type }])
    }
  }

  const removeRowAOverride = (index: number) => {
    const currentOverrides = watchedValues.row_a_overrides || []
    setValue('row_a_overrides', currentOverrides.filter(o => o.index !== index))
  }

  const updateRowAUnitDoors = (index: number, doors: UnitDoorsConfig) => {
    const currentOverrides = watchedValues.row_a_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], doors }
      setValue('row_a_overrides', updated)
    } else {
      setValue('row_a_overrides', [...currentOverrides, { index, doors }])
    }
  }

  // Row B unit helpers
  const toggleRowBUnit = (index: number) => {
    const newOpen = new Set(openRowBUnits)
    if (newOpen.has(index)) {
      newOpen.delete(index)
    } else {
      newOpen.add(index)
    }
    setOpenRowBUnits(newOpen)
  }

  const getRowBOverride = (index: number) => {
    return watchedValues.row_b_overrides?.find(o => o.index === index)
  }

  const addRowBWidthOverride = (index: number, width: number) => {
    const currentOverrides = watchedValues.row_b_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], width_ft: width }
      setValue('row_b_overrides', updated)
    } else {
      setValue('row_b_overrides', [...currentOverrides, { index, width_ft: width }])
    }
  }

  const addRowBTypeOverride = (index: number, type: UnitType) => {
    const currentOverrides = watchedValues.row_b_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], type }
      setValue('row_b_overrides', updated)
    } else {
      setValue('row_b_overrides', [...currentOverrides, { index, type }])
    }
  }

  const removeRowBOverride = (index: number) => {
    const currentOverrides = watchedValues.row_b_overrides || []
    setValue('row_b_overrides', currentOverrides.filter(o => o.index !== index))
  }

  const updateRowBUnitDoors = (index: number, doors: UnitDoorsConfig) => {
    const currentOverrides = watchedValues.row_b_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], doors }
      setValue('row_b_overrides', updated)
    } else {
      setValue('row_b_overrides', [...currentOverrides, { index, doors }])
    }
  }

  // Get effective door configuration for unit
  const getEffectiveUnitDoors = (rowId: 'A' | 'B', index: number): UnitDoorsConfig => {
    const unitWidths = rowId === 'A' ? layoutResult.rowA.widths : layoutResult.rowB.widths
    const unitWidth = unitWidths[index] ?? 0
    const unitCount = rowId === 'A' ? watchedValues.row_a.unit_count : effectiveRowB.unit_count
    return getUnitDoorsFromParams(watchedValues, rowId, index, unitWidth, unitCount)
  }

  // Copy one unit's doors to all units in both rows
  const copyDoorsToAllRowUnits = (rowId: 'A' | 'B', sourceIndex: number) => {
    const sourceDoors = getEffectiveUnitDoors(rowId, sourceIndex)

    const currentRowA = watchedValues.row_a_overrides || []
    const newRowA = Array.from({ length: watchedValues.row_a.unit_count }, (_, i) => ({
      index: i,
      width_ft: currentRowA.find(o => o.index === i)?.width_ft,
      doors: sourceDoors,
    }))
    setValue('row_a_overrides', newRowA)

    const currentRowB = watchedValues.row_b_overrides || []
    const newRowB = Array.from({ length: effectiveRowB.unit_count }, (_, i) => ({
      index: i,
      width_ft: currentRowB.find(o => o.index === i)?.width_ft,
      doors: sourceDoors,
    }))
    setValue('row_b_overrides', newRowB)
  }

  // Calculate interior depths for door configuration
  const rowAInteriorDepth = watchedValues.row_a.depth_ft - watchedValues.partition_wall_thickness_ft
  const rowBInteriorDepth = effectiveRowB.depth_ft - watchedValues.partition_wall_thickness_ft

  // Show position
  const latitude = previewLatitude?.toFixed(6) || watchedValues.latitude?.toFixed(6) || '—'
  const longitude = previewLongitude?.toFixed(6) || watchedValues.longitude?.toFixed(6) || '—'

  const onSubmit = async (values: DoubleRowParams) => {
    // Validate pricing form
    const pricingValid = await pricingForm.trigger()
    if (!pricingValid) return

    if (previewLatitude !== undefined && previewLongitude !== undefined) {
      values.latitude = previewLatitude
      values.longitude = previewLongitude
    }
    onSave(values, pricingForm.getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full flex-1 min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-6">
          {/* Layout Mode Selection */}
          <div className="bg-purple-50 dark:bg-purple-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-purple-700 dark:text-purple-300 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Layout Mode
            </h3>
            <p className="text-xs text-muted-foreground">Configure row relationship</p>
          </div>

          <RadioGroup
            value={watchedValues.layout_mode}
            onValueChange={(value) => handleLayoutModeChange(value as LayoutMode)}
            className="grid grid-cols-2 gap-3"
          >
            <div>
              <RadioGroupItem value="symmetric" id="symmetric" className="peer sr-only" />
              <Label
                htmlFor="symmetric"
                className={cn(
                  "flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer",
                  watchedValues.layout_mode === 'symmetric' && "border-primary bg-primary/5"
                )}
              >
                <ArrowLeftRight className="h-6 w-6 mb-2" />
                <span className="font-medium text-sm">Symmetric</span>
                <span className="text-xs text-muted-foreground text-center">Both rows identical</span>
              </Label>
            </div>
            <div>
              <RadioGroupItem value="custom" id="custom" className="peer sr-only" />
              <Label
                htmlFor="custom"
                className={cn(
                  "flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer",
                  watchedValues.layout_mode === 'custom' && "border-primary bg-primary/5"
                )}
              >
                <Layers className="h-6 w-6 mb-2" />
                <span className="font-medium text-sm">Custom</span>
                <span className="text-xs text-muted-foreground text-center">Independent config</span>
              </Label>
            </div>
          </RadioGroup>

          <Separator />

          {/* Building Section Header */}
          <div className="bg-blue-50 dark:bg-blue-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-blue-700 dark:text-blue-300">Building Envelope</h3>
            <p className="text-xs text-muted-foreground">Overall structure dimensions</p>
          </div>

          {/* Identity Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Identity</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} placeholder="e.g., Double Row Hangar A" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input id="code" {...register('code')} placeholder="e.g., DR-A" />
                {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
              </div>
            </div>
          </div>

          <Separator />

          {/* Dimensions Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Dimensions (feet)</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="width_ft">Width</Label>
                <Input
                  id="width_ft"
                  type="number"
                  step="any"
                  {...register('width_ft', { valueAsNumber: true })}
                />
                {errors.width_ft && (
                  <p className="text-xs text-destructive">{errors.width_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="height_ft">Height</Label>
                <Input
                  id="height_ft"
                  type="number"
                  step="any"
                  {...register('height_ft', { valueAsNumber: true })}
                />
                {errors.height_ft && (
                  <p className="text-xs text-destructive">{errors.height_ft.message}</p>
                )}
              </div>
            </div>

            {/* Calculated building depth */}
            <div className="flex justify-between text-sm p-2 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Total Depth (calculated):</span>
              <span className="font-medium">{buildingDepth.toFixed(1)} ft</span>
            </div>

            {/* Wall Thickness */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="shared_wall_thickness_ft">Shared Wall (ft)</Label>
                <Input
                  id="shared_wall_thickness_ft"
                  type="number"
                  step="any"
                  min={0.5}
                  max={3}
                  {...register('shared_wall_thickness_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">Spine wall between rows</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="partition_wall_thickness_ft">Partition Wall (ft)</Label>
                <Input
                  id="partition_wall_thickness_ft"
                  type="number"
                  step="any"
                  min={0.25}
                  max={2}
                  {...register('partition_wall_thickness_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">Between units</p>
              </div>
            </div>

          </div>

          <Separator />

          {/* Row A Configuration */}
          <div className="bg-green-50 dark:bg-green-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-green-700 dark:text-green-300">
              Row A (North-Facing)
            </h3>
            <p className="text-xs text-muted-foreground">Primary doors face north</p>
          </div>

          <div className="space-y-4">
            {/* Row A depth */}
            <div className="space-y-2">
              <Label htmlFor="row_a_depth_ft">Unit Depth (ft)</Label>
              <Input
                id="row_a_depth_ft"
                type="number"
                step="any"
                min={30}
                max={200}
                {...register('row_a.depth_ft', { valueAsNumber: true })}
              />
              {errors.row_a?.depth_ft && (
                <p className="text-xs text-destructive">{errors.row_a.depth_ft.message}</p>
              )}
            </div>

            {/* Row A unit count slider */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Number of Units</Label>
                <span className="text-sm font-medium">{watchedValues.row_a.unit_count}</span>
              </div>
              <Slider
                value={[watchedValues.row_a.unit_count]}
                onValueChange={handleRowAUnitCountChange}
                min={1}
                max={20}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span>
                <span>20</span>
              </div>
            </div>

            {/* Row A Layout Preview */}
            <div className="space-y-2">
              <Label>Row A Layout</Label>
              <UnitLayoutPreview
                unitWidths={layoutResult.rowA.widths}
                buildingWidth={watchedValues.width_ft}
                wallThickness={watchedValues.partition_wall_thickness_ft}
              />
            </div>

            {/* Row A Constraint Warning */}
            {!layoutResult.rowA.isBalanced && (
              <ConstraintWarning
                difference={layoutResult.rowA.difference_ft}
                buildingWidth={watchedValues.width_ft}
                suggestedFixes={layoutResult.suggestedFixes.filter(f => f.row === 'A' || f.row === 'both')}
                onApplyFix={handleApplyFix}
              />
            )}

            {/* Row A Unit Adjustments */}
            <Collapsible
              open={expandedSections.has('row_a_units')}
              onOpenChange={() => toggleSection('row_a_units')}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-between w-full p-2 text-sm hover:bg-muted/50 rounded-md"
                >
                  <span className="font-medium">Individual Unit Adjustments</span>
                  {expandedSections.has('row_a_units') ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 pt-2">
                  {Array.from({ length: watchedValues.row_a.unit_count }, (_, i) => {
                    const override = getRowAOverride(i)
                    const unitWidth = override?.width_ft ?? layoutResult.rowA.widths[i]
                    const isOpen = openRowAUnits.has(i)
                    const hasOverride = override !== undefined

                    return (
                      <Collapsible key={i} open={isOpen} onOpenChange={() => toggleRowAUnit(i)}>
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              'w-full flex items-center justify-between p-2 rounded-md text-sm',
                              'hover:bg-muted/50 transition-colors',
                              hasOverride && 'bg-amber-50 dark:bg-amber-950/30'
                            )}
                          >
                            <div className="flex items-center gap-2">
                              {isOpen ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                              <span>A-{String(i + 1).padStart(2, '0')}</span>
                              {hasOverride && (
                                <span className="text-xs text-amber-600 dark:text-amber-400">(custom)</span>
                              )}
                            </div>
                            <span className="text-muted-foreground">{unitWidth?.toFixed(1)} ft</span>
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-3 border rounded-md mt-1 space-y-4">
                            {/* Unit Type Override */}
                            <div className="space-y-2">
                              <Label>Unit Type</Label>
                              <Select
                                value={override?.type || 'box_hangar'}
                                onValueChange={(value) => addRowATypeOverride(i, value as UnitType)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ALLOWED_UNIT_TYPES_FOR_AREA.box_hangar_double_row.map((t) => (
                                    <SelectItem key={t} value={t}>
                                      {UNIT_TYPE_LABELS[t]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Width Override */}
                            <div className="space-y-2">
                              <Label>Width Override (ft)</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="number"
                                  step="any"
                                  value={override?.width_ft ?? layoutResult.rowA.widths[i] ?? 0}
                                  onChange={(e) => addRowAWidthOverride(i, parseFloat(e.target.value))}
                                  min={15}
                                  className="flex-1"
                                />
                                {hasOverride && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeRowAOverride(i)}
                                    title="Remove override"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            <Separator />

                            {/* Door Configuration */}
                            <DoubleRowUnitDoorEditor
                              rowId="A"
                              unitIndex={i}
                              unitCount={watchedValues.row_a.unit_count}
                              doors={getEffectiveUnitDoors('A', i)}
                              onChange={(doors) => updateRowAUnitDoors(i, doors)}
                              unitWidthFt={unitWidth ?? 0}
                              unitDepthFt={rowAInteriorDepth}
                              unitHeightFt={watchedValues.height_ft}
                              onCopyToAll={() => copyDoorsToAllRowUnits('A', i)}
                            />
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <Separator />

          {/* Row B Configuration */}
          <div className="bg-orange-50 dark:bg-orange-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-orange-700 dark:text-orange-300">
              Row B (South-Facing)
              {watchedValues.layout_mode === 'symmetric' && (
                <span className="ml-2 text-xs font-normal">(Mirrors Row A)</span>
              )}
            </h3>
            <p className="text-xs text-muted-foreground">Primary doors face south</p>
          </div>

          {watchedValues.layout_mode === 'symmetric' ? (
            <div className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-md">
              <p>Row B automatically mirrors Row A in symmetric mode.</p>
              <p className="mt-1">Switch to Custom mode to configure independently.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Row B depth */}
              <div className="space-y-2">
                <Label htmlFor="row_b_depth_ft">Unit Depth (ft)</Label>
                <Input
                  id="row_b_depth_ft"
                  type="number"
                  step="any"
                  min={30}
                  max={200}
                  {...register('row_b.depth_ft', { valueAsNumber: true })}
                />
                {errors.row_b?.depth_ft && (
                  <p className="text-xs text-destructive">{errors.row_b.depth_ft.message}</p>
                )}
              </div>

              {/* Row B unit count slider */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Number of Units</Label>
                  <span className="text-sm font-medium">{watchedValues.row_b.unit_count}</span>
                </div>
                <Slider
                  value={[watchedValues.row_b.unit_count]}
                  onValueChange={handleRowBUnitCountChange}
                  min={1}
                  max={20}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1</span>
                  <span>20</span>
                </div>
              </div>

              {/* Row B Layout Preview */}
              <div className="space-y-2">
                <Label>Row B Layout</Label>
                <UnitLayoutPreview
                  unitWidths={layoutResult.rowB.widths}
                  buildingWidth={watchedValues.width_ft}
                  wallThickness={watchedValues.partition_wall_thickness_ft}
                />
              </div>

              {/* Row B Constraint Warning */}
              {!layoutResult.rowB.isBalanced && (
                <ConstraintWarning
                  difference={layoutResult.rowB.difference_ft}
                  buildingWidth={watchedValues.width_ft}
                  suggestedFixes={layoutResult.suggestedFixes.filter(f => f.row === 'B' || f.row === 'both')}
                  onApplyFix={handleApplyFix}
                />
              )}

              {/* Row B Unit Adjustments */}
              <Collapsible
                open={expandedSections.has('row_b_units')}
                onOpenChange={() => toggleSection('row_b_units')}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full p-2 text-sm hover:bg-muted/50 rounded-md"
                  >
                    <span className="font-medium">Individual Unit Adjustments</span>
                    {expandedSections.has('row_b_units') ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-2 pt-2">
                    {Array.from({ length: effectiveRowB.unit_count }, (_, i) => {
                      const override = getRowBOverride(i)
                      const unitWidth = override?.width_ft ?? layoutResult.rowB.widths[i]
                      const isOpen = openRowBUnits.has(i)
                      const hasOverride = override !== undefined

                      return (
                        <Collapsible key={i} open={isOpen} onOpenChange={() => toggleRowBUnit(i)}>
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                'w-full flex items-center justify-between p-2 rounded-md text-sm',
                                'hover:bg-muted/50 transition-colors',
                                hasOverride && 'bg-amber-50 dark:bg-amber-950/30'
                              )}
                            >
                              <div className="flex items-center gap-2">
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                <span>B-{String(i + 1).padStart(2, '0')}</span>
                                {hasOverride && (
                                  <span className="text-xs text-amber-600 dark:text-amber-400">(custom)</span>
                                )}
                              </div>
                              <span className="text-muted-foreground">{unitWidth?.toFixed(1)} ft</span>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="p-3 border rounded-md mt-1 space-y-4">
                              {/* Unit Type Override */}
                              <div className="space-y-2">
                                <Label>Unit Type</Label>
                                <Select
                                  value={override?.type || 'box_hangar'}
                                  onValueChange={(value) => addRowBTypeOverride(i, value as UnitType)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ALLOWED_UNIT_TYPES_FOR_AREA.box_hangar_double_row.map((t) => (
                                      <SelectItem key={t} value={t}>
                                        {UNIT_TYPE_LABELS[t]}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Width Override */}
                              <div className="space-y-2">
                                <Label>Width Override (ft)</Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="number"
                                    step="any"
                                    value={override?.width_ft ?? layoutResult.rowB.widths[i] ?? 0}
                                    onChange={(e) => addRowBWidthOverride(i, parseFloat(e.target.value))}
                                    min={15}
                                    className="flex-1"
                                  />
                                  {hasOverride && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeRowBOverride(i)}
                                      title="Remove override"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              <Separator />

                              {/* Door Configuration */}
                              <DoubleRowUnitDoorEditor
                                rowId="B"
                                unitIndex={i}
                                unitCount={effectiveRowB.unit_count}
                                doors={getEffectiveUnitDoors('B', i)}
                                onChange={(doors) => updateRowBUnitDoors(i, doors)}
                                unitWidthFt={unitWidth ?? 0}
                                unitDepthFt={rowBInteriorDepth}
                                unitHeightFt={watchedValues.height_ft}
                                onCopyToAll={() => copyDoorsToAllRowUnits('B', i)}
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          <Separator />

          {/* Default Door Config Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Default Door Configuration</h4>
            <p className="text-xs text-muted-foreground">Applied to all units unless overridden</p>

            <div className="space-y-2">
              <Label htmlFor="default_door_type">Door Type</Label>
              <Select
                value={watchedValues.default_door_type}
                onValueChange={(value) => setValue('default_door_type', value as DoorType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bifold">Bi-fold (30-100 ft)</SelectItem>
                  <SelectItem value="sliding">Sliding (40-200 ft)</SelectItem>
                  <SelectItem value="hydraulic">Hydraulic (50-150 ft)</SelectItem>
                  <SelectItem value="rolling">Rolling (20-60 ft)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="default_door_width_ft">Door Width (ft)</Label>
                <Input
                  id="default_door_width_ft"
                  type="number"
                  step="any"
                  min={doorConstraints.min_width_ft}
                  max={doorConstraints.max_width_ft}
                  {...register('default_door_width_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">
                  {doorConstraints.min_width_ft}-{doorConstraints.max_width_ft} ft
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_door_height_ft">Door Height (ft)</Label>
                <Input
                  id="default_door_height_ft"
                  type="number"
                  step="any"
                  {...register('default_door_height_ft', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Numbering Style */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Unit Numbering</h4>

            <Select
              value={watchedValues.numbering_style}
              onValueChange={(value) => setValue('numbering_style', value as 'paired' | 'sequential' | 'custom')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paired">Paired (A-01/B-01, A-02/B-02)</SelectItem>
                <SelectItem value="sequential">Sequential (01, 02, 03...)</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Appearance Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Appearance</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="floor_color">Floor Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="floor_color"
                    type="color"
                    {...register('floor_color')}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input {...register('floor_color')} className="flex-1" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wall_color">Wall Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="wall_color"
                    type="color"
                    {...register('wall_color')}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input {...register('wall_color')} className="flex-1" />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Position Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Position</h4>
            <p className="text-xs text-muted-foreground">Drag the building on the map to set position</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{latitude}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{longitude}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bearing_deg">Rotation (degrees)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="bearing_deg"
                  type="number"
                  step="any"
                  min={0}
                  max={360}
                  {...register('bearing_deg', { valueAsNumber: true })}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">°</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Pricing & Building Features */}
          <Form {...pricingForm}>
            <PricingFeaturesSection form={pricingForm} />
          </Form>

          {/* Remove Area - only in edit mode */}
          {!isCreating && areaId && onRemoved && (
            <RemoveAreaButton
              areaId={areaId}
              areaName={areaName || 'this area'}
              onRemoved={onRemoved}
            />
          )}
        </div>
      </ScrollArea>

      {/* Sticky footer with actions */}
      <div className="border-t p-4 flex gap-2 shrink-0">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" className="flex-1" disabled={!layoutResult.isBalanced || isSaving || !isPlaced}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            isCreating ? 'Create Building' : 'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}
